import type { GlanceCell, SectionDef, Stages } from "../order-sim/engine"
import { choseCell, draftTail, NUMBER_WORDS, rupees } from "../order-sim/model"
import type { Row, RowChangedPayload } from "../order-sim/runThrough"
import type { Stage } from "./contract"

/**
 * The OIA decisions page's content (handoff 4: "same page, stages intake → grounds → preliminary →
 * findings → relief → drafting; different row content"): the numbered sections, the verdict line,
 * the glance, and what a change re-runs. Every figure is one the engine's records carry.
 */

export const SECTIONS: (SectionDef & { stage: Stage })[] = [
  { stage: "intake", n: "01", title: "The appeal", now: "Reading the order, the appeal and the record." },
  { stage: "grounds", n: "02", title: "Grounds", now: "Setting out each ground as a point to decide." },
  { stage: "preliminary", n: "03", title: "Preliminary", now: "Working the time limit and the pre-deposit." },
  { stage: "findings", n: "04", title: "Findings", now: "Deciding each ground on the record." },
  { stage: "relief", n: "05", title: "Relief", now: "Working out what the order sets aside." },
]
export const DRAFTING_NOW = "Drafting the sections and checking them."

const decided = (stages: Stages) => (stages.findings?.rows ?? []).filter((r) => r.call)
const outcomeRow = (stages: Stages) => stages.relief?.rows.find((r) => r.id === "outcome")
const disposal = (stages: Stages) => stages.preliminary?.rows.find((r) => r.id === "disposal")?.text ?? ""
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** "Two of 3 grounds are accepted. The appeal is partly allowed: ₹60,000 is set aside and ₹40,000 stands. The draft is written and checked." */
export function verdict(stages: Stages): string | null {
  const said = outcome(stages)
  return said ? `${said} ${draftTail(stages)}` : null
}

/** The verdict without the draft's state: what the order decides, once the relief landed. */
export function outcome(stages: Stages): string | null {
  const relief = outcomeRow(stages)
  if (!relief) return null
  const said = relief.text.split(" · ")[0].replace(/^Appeal /, "The appeal is ")
  if (disposal(stages).startsWith("rejected")) return `${said}. The grounds are not reached.`
  const found = decided(stages)
  const won = found.filter((r) => r.call === "accepted").length
  const part = found.filter((r) => r.call === "partly_accepted").length
  const of = found.length
  const lost = of - won - part
  const lead = !of ? ""
    : won === of ? `${of === 1 ? "The ground is" : `All ${of} grounds are`} accepted.`
    : lost === of ? `${of === 1 ? "The ground is" : `All ${of} grounds are`} rejected.`
    : `${won ? `${NUMBER_WORDS[won] ?? won} of ${of} ${plural(of, "ground", "grounds")} ${plural(won, "is", "are")} accepted` : `No ground is accepted in full`}${part ? `, ${part} in part` : ""}.`
  const aside = relief.amount ?? 0
  const stands = relief.sustained ?? 0
  const money = aside && stands ? `: ${rupees(aside)} is set aside and ${rupees(stands)} stands`
    : aside ? `: ${rupees(aside)} is set aside` : stands ? `: ${rupees(stands)} stands` : ""
  return `${lead ? `${lead} ` : ""}${said}${money}.`
}

export function glance(stages: Stages, changes: RowChangedPayload[]): GlanceCell[] {
  const found = decided(stages)
  const outcome = outcomeRow(stages)
  const accepted = found.filter((r) => r.call !== "rejected")
  const appellant = stages.intake?.rows.find((r) => r.id === "appellant")
  return [
    {
      label: "Grounds", value: `${found.length} · ${accepted.length} accepted`,
      sub: [found.filter((r) => r.call === "rejected").map((r) => r.id).join(", ") + (found.some((r) => r.call === "rejected") ? " rejected" : ""),
        found.filter((r) => r.call === "partly_accepted").map((r) => r.id).join(", ") + (found.some((r) => r.call === "partly_accepted") ? " in part" : "")].filter((x) => x.trim()).join(" · ") || (disposal(stages).startsWith("rejected") ? "not reached" : ""),
    },
    { label: "Outcome", value: outcome?.text.split(" · ")[0].replace(/^Appeal /, "") ?? "—", sub: outcome?.text.split(" · ")[1] ?? (appellant?.sub ?? "") },
    { label: "Set aside", value: outcome ? rupees(outcome.amount ?? 0) : "—", mono: true, sub: outcome ? `${rupees(outcome.sustained ?? 0)} stands` : "" },
    choseCell(SECTIONS, stages, changes),
  ]
}

const SECTIONS_OF: Partial<Record<Stage, string[]>> = {
  intake: ["the caption and preamble", "the parties", "the facts"],
  grounds: ["the grounds of appeal", "the points for determination"],
  preliminary: ["the maintainability section"],
  findings: ["the finding sections", "the hearing"],
  relief: ["the Order section", "the closing"],
}
const DOWN: Partial<Record<Stage, Stage[]>> = {
  intake: ["grounds", "preliminary", "findings", "relief"],
  grounds: ["preliminary", "findings", "relief"],
  preliminary: ["findings", "relief"],
  findings: ["relief"],
  relief: [],
}
const SECONDS: Partial<Record<Stage, number>> = { intake: 30, grounds: 20, preliminary: 18, findings: 50, relief: 20 }

/** "Re-runs: 05 Relief · the G1 finding section · the Order section · the closing · the self-check. about 70 s. 01 to 03 and the other findings stay." */
export function rerunLine(stage: string, row: Row): string {
  const st = stage as Stage
  const later = DOWN[st] ?? []
  // One ground's call re-runs its own finding, not the hearing or the other grounds.
  const gid = st === "findings" && /^G\d+$/.test(row.id) ? row.id : ""
  const parts: string[] = []
  for (const s of later) {
    const sec = SECTIONS.find((x) => x.stage === s)
    if (sec) parts.push(`${sec.n} ${sec.title}`)
  }
  const sections = new Set<string>()
  for (const s of [st, ...later]) {
    for (const x of SECTIONS_OF[s] ?? []) {
      if (gid && x === "the hearing") continue
      sections.add(gid && x === "the finding sections" ? `the ${gid} finding section` : x)
    }
  }
  parts.push(...sections, "the self-check")
  const seconds = (gid ? 15 : SECONDS[st] ?? 0) + later.reduce((n, s) => n + (SECONDS[s] ?? 0), 0) + 6 * sections.size + 8
  const before = SECTIONS.findIndex((x) => x.stage === st)
  const stays = before > 0 ? `${before === 1 ? "01" : `01 to ${SECTIONS[before - 1].n}`}${gid ? " and the other findings" : ""} stay.` : ""
  return `Re-runs: ${parts.join(" · ")}. about ${Math.max(10, Math.round(seconds / 10) * 10)} s. ${stays}`.trim()
}
